import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { v4 as uuidv4 } from 'uuid';

import { Header } from 'components/Header';
import { RequiredInfoFields, RequiredInfoValues } from '../components/RequiredInfoFields';
import { useServiceConfigs } from '../context/ServiceConfigContext';
import {
  buildInitialRequiredValues,
  sanitizeRequiredValues,
  ENGRAVING_CHARACTER_COUNT_LABEL,
  ensureMinimumEngravingCharacterCount,
  getEngravingConstraints,
} from '../utils/requiredInfo';

const formatLocationLabel = (location: string) =>
  location.replace(/Kerrville/g, 'Product Care').replace(/Store/g, 'In Store');

const CustomerOwnedAddServicePage: React.FC = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const location = useLocation() as {
    state?: { metalType?: string; productType?: string; measuredSize?: string; condition?: string };
  };

  const { configs, selectedServices, setSelectedServices, serviceType, storeId } = useServiceConfigs();

  const metalType = location.state?.metalType;
  const productType = location.state?.productType;
  const measuredSize = location.state?.measuredSize;
  const condition = location.state?.condition;

  const config = useMemo(() => configs?.find((item) => item.id === serviceId), [configs, serviceId]);

  const engravingConstraints = useMemo(
    () => getEngravingConstraints(config?.requiredInfo),
    [config?.requiredInfo],
  );

  const isEngravingService = useMemo(
    () =>
      Boolean(
        config?.requiredInfo?.some((field) => field.label === ENGRAVING_CHARACTER_COUNT_LABEL),
      ),
    [config?.requiredInfo],
  );

  const [selectedLocation, setSelectedLocation] = useState('');
  const [quantityInput, setQuantityInput] = useState('1');
  const [serviceNote, setServiceNote] = useState('');
  const [requiredValues, setRequiredValues] = useState<RequiredInfoValues>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);

  const navigationState = useMemo(
    () => ({
      metalType,
      productType,
      measuredSize,
      condition,
    }),
    [metalType, productType, measuredSize, condition],
  );

  const isQuick = serviceType === 'QUICK';
  const isCs1Store = storeId?.toUpperCase?.() === 'CS1';

  const locations = useMemo(() => {
    const tokens = (config?.locationServiced ?? [])
      .flatMap((loc) => String(loc).split(',').map((value) => value.trim()))
      .filter(Boolean);
    if (!tokens.length) {
      return [];
    }
    const productCareLocation =
      tokens.find((value) => /Kerrville/i.test(value) || /Product\s*Care/i.test(value)) ?? tokens[0];
    if (isCs1Store) {
      return productCareLocation ? [productCareLocation] : [];
    }
    if (isQuick) {
      return tokens.includes('Store') ? ['Store'] : tokens;
    }
    return tokens;
  }, [config?.locationServiced, isCs1Store, isQuick]);

  useEffect(() => {
    if (!config) return;
    const defaults = buildInitialRequiredValues(config.requiredInfo);
    setRequiredValues(defaults);
    setFieldErrors({});
    setServiceNote('');
    setQuantityInput('1');
  }, [config]);

  useEffect(() => {
    if (!locations.length) {
      setSelectedLocation('');
      return;
    }
    setSelectedLocation((current) => {
      if (current && locations.includes(current)) {
        return current;
      }
      if (locations.length === 1) {
        return locations[0];
      }
      return '';
    });
  }, [locations]);

  if (!config) {
    return (
      <Box p={4}>
        <Typography color="error">Service not found.</Typography>
      </Box>
    );
  }

  const { description, sku, cost, allowQuantity } = config;

  const validate = () => {
    if (!selectedLocation) {
      setError('Please choose where the work will be completed.');
      return false;
    }

    if (allowQuantity && !isEngravingService) {
      const numericQuantity = Number.parseInt(quantityInput, 10);
      if (!Number.isInteger(numericQuantity) || numericQuantity < 1) {
        setError('Quantity must be at least 1.');
        return false;
      }
    }

    if (config.requiredInfo?.length) {
      const nextErrors: Record<string, string> = {};
      const previouslyEngraved = requiredValues['Previously Engraved'] === 'true';
      config.requiredInfo.forEach((field) => {
        const raw = requiredValues[field.label];
        if (field.valueType === 'Boolean') return;
        if (field.label === 'Previous Location' && !previouslyEngraved) {
          return;
        }
        if (field.valueType === 'Number') {
          if (raw == null || raw === '') {
            nextErrors[field.label] = 'Required';
            return;
          }
          const numeric = Number(raw);
          if (Number.isNaN(numeric)) {
            nextErrors[field.label] = 'Enter a valid number';
            return;
          }
          if (field.label === ENGRAVING_CHARACTER_COUNT_LABEL) {
            if (!Number.isInteger(numeric) || numeric < engravingConstraints.min) {
              nextErrors[field.label] = `Must be a whole number of at least ${engravingConstraints.min}`;
            }
            return;
          }
          if (numeric < 0) {
            nextErrors[field.label] = 'Enter a valid number';
          }
          return;
        }
        if (!raw || !raw.trim()) {
          nextErrors[field.label] = 'Required';
        }
      });
      setFieldErrors(nextErrors);
      if (Object.keys(nextErrors).length) {
        setError('Please complete the required information.');
        return false;
      }
    }

    setFieldErrors({});
    setError(null);
    return true;
  };

  const handleSubmit = (goToSummary: boolean) => {
    if (!validate()) return;

    const sanitized = sanitizeRequiredValues(config.requiredInfo, requiredValues);
    let finalQuantity = 1;
    if (isEngravingService) {
      const parsedCharacters = Number.parseInt(
        sanitized?.[ENGRAVING_CHARACTER_COUNT_LABEL] ??
        ensureMinimumEngravingCharacterCount(
          requiredValues[ENGRAVING_CHARACTER_COUNT_LABEL],
          engravingConstraints,
        ),
        10,
      );
      finalQuantity = Number.isFinite(parsedCharacters)
        ? Math.max(engravingConstraints.min, parsedCharacters)
        : engravingConstraints.min;
    } else if (allowQuantity) {
      const parsedQuantity = Number.parseInt(quantityInput, 10);
      finalQuantity = Math.max(1, parsedQuantity || 1);
    }

    const selection = {
      lineId: uuidv4(),
      id: config.id,
      sku: config.sku,
      description: config.description,
      location: selectedLocation,
      quantity: finalQuantity,
      ...(serviceNote.trim() ? { serviceNote: serviceNote.trim() } : {}),
      ...(sanitized ? { requiredInfoValues: sanitized } : {}),
    };

    setSelectedServices([...selectedServices, selection]);
    setSnack('Service added to cart');

    if (goToSummary) {
      navigate('/summary', { state: navigationState });
    } else {
      navigate('/service', {
        state: { ...navigationState, service: serviceType ?? 'STANDARD' },
      });
    }
  };

  const onRequiredChange = (label: string, value: string) => {
    setRequiredValues((prev) => {
      return { ...prev, [label]: value };
    });
  };

  const engravingQuantity = useMemo(() => {
    if (!isEngravingService) return null;
    const ensuredValue = ensureMinimumEngravingCharacterCount(
      requiredValues[ENGRAVING_CHARACTER_COUNT_LABEL],
      engravingConstraints,
    );
    const parsed = Number.parseInt(ensuredValue, 10);
    if (!Number.isFinite(parsed)) {
      return engravingConstraints.min;
    }
    return Math.max(engravingConstraints.min, parsed);
  }, [engravingConstraints, isEngravingService, requiredValues]);

  const effectiveQuantity = useMemo(() => {
    if (isEngravingService) {
      return engravingQuantity ?? engravingConstraints.min;
    }
    if (!allowQuantity) return 1;
    const parsed = Number.parseInt(quantityInput, 10);
    if (!Number.isFinite(parsed) || parsed < 1) return 1;
    return parsed;
  }, [allowQuantity, isEngravingService, quantityInput, engravingQuantity]);

  const totalCost = useMemo(() => cost * effectiveQuantity, [cost, effectiveQuantity]);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header
        title={<Typography variant="h6">{description}</Typography>}
        subtitle={`SKU • ${sku}`}
        showBackButton
        onBack={() => navigate(-1)}
        align="left"
        sticky
        elevateOnScroll
        serviceCount={selectedServices.length}
        onBadgeClick={() => navigate('/summary', { state: navigationState })}
        rightSlot={
          <Chip
            size="small"
            icon={<AttachMoneyOutlinedIcon />}
            label={`$${cost.toFixed(2)}`}
            variant="outlined"
            color="primary"
            sx={{ borderRadius: 2 }}
          />
        }
      />

      <Card
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        })}
      >
        <CardContent sx={{ py: 2 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Metal: ${metalType ?? '—'}`} variant="outlined" />
            <Chip label={`Product: ${productType ?? '—'}`} variant="outlined" />
            <Chip label={`Measured Size: ${measuredSize ?? '—'}`} variant="outlined" />
            <Chip label={`Condition: ${condition ?? '—'}`} variant="outlined" />
            <Chip
              icon={isQuick ? <BoltOutlinedIcon /> : <ScheduleOutlinedIcon />}
              label={isQuick ? 'Quick Service' : 'Standard Service'}
              variant="outlined"
              color={isQuick ? 'primary' : 'default'}
            />
          </Stack>
        </CardContent>
      </Card>

      <Paper
        elevation={0}
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          mb: 4,
          p: 2,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
          background: alpha(theme.palette.primary.light, 0.05),
          backdropFilter: 'saturate(120%) blur(4px)',
        })}
      >
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={600}>
              Choose location
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {locations.map((loc) => (
                <Button
                  key={loc}
                  variant={selectedLocation === loc ? 'contained' : 'outlined'}
                  startIcon={<PlaceOutlinedIcon />}
                  onClick={() => setSelectedLocation(loc)}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {formatLocationLabel(loc)}
                </Button>
              ))}
            </Stack>
          </Stack>

           <RequiredInfoFields
            fields={config.requiredInfo}
            values={requiredValues}
            onChange={onRequiredChange}
            errors={fieldErrors}
          />

          {allowQuantity && !isEngravingService && (
            <TextField
              label="Quantity"
              type="number"
              value={quantityInput}
              onChange={(event) => setQuantityInput(event.target.value)}
              onBlur={(event) => {
                const value = event.target.value.trim();
                if (!value) {
                  setQuantityInput('1');
                  return;
                }
                const numeric = Number.parseInt(value, 10);
                if (!Number.isFinite(numeric) || numeric < 1) {
                  setQuantityInput('1');
                } else {
                  setQuantityInput(String(numeric));
                }
              }}
              InputProps={{
                inputProps: { min: 1, step: 1, inputMode: 'numeric', pattern: '[0-9]*' },
              }}
            />
          )}

          <TextField
            label="Notes"
            placeholder="Add any service notes (optional)"
            value={serviceNote}
            onChange={(event) => setServiceNote(event.target.value)}
            multiline
            minRows={3}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <NotesOutlinedIcon sx={{ mr: 1 }} />
                </InputAdornment>
              ),
            }}
          />

          <Chip
            icon={<AttachMoneyOutlinedIcon />}
            label={`Total cost: $${totalCost.toFixed(2)}`}
            color="primary"
            variant="outlined"
            sx={(theme) => ({
              alignSelf: 'flex-start',
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderColor: alpha(theme.palette.primary.main, 0.4),
              color: theme.palette.primary.main,
              '& .MuiChip-icon': {
                color: theme.palette.primary.main,
              },
            })}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              fullWidth
              color="primary"
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => handleSubmit(false)}
            >
              Add another service to this item
            </Button>
            <Button
              fullWidth
              color="primary"
              variant="outlined"
              endIcon={<ArrowForwardIcon />}
              onClick={() => handleSubmit(true)}
            >
              Add and review summary
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Snackbar
        open={Boolean(snack)}
        message={snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
      />
      <Snackbar
        open={Boolean(error)}
        message={error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
      />
    </Box>
  );
};

export default CustomerOwnedAddServicePage;
